import { Component, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { AuthPageLayoutComponent } from '../../../layout/auth-page-layout/auth-page-layout.component';
import { AgeVerificationService } from '../../../shared/services/age-verification.service';

type ViewState = 'checking' | 'approved' | 'in-progress' | 'failed' | 'expired' | 'error';

/** VerifyMy can take a moment to finalise after redirecting the user back. */
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 8;

/**
 * /age-verification/callback?verification_id=…
 *
 * VerifyMy's redirect_url. We hand the id to our API, which asks VerifyMy for
 * the real result — the query string alone never grants access.
 */
@Component({
  selector: 'app-age-verification-callback',
  standalone: true,
  imports: [RouterModule, AuthPageLayoutComponent],
  templateUrl: './age-verification-callback.html',
})
export class AgeVerificationCallback implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ageVerification = inject(AgeVerificationService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly state = signal<ViewState>('checking');
  readonly errorMessage = signal('');

  private verificationId = '';
  private polls = 0;
  private pending?: Subscription;

  ngOnInit(): void {
    if (!this.isBrowser) return;

    this.verificationId = this.route.snapshot.queryParamMap.get('verification_id') ?? '';
    if (!this.verificationId) {
      this.router.navigate(['/age-verification'], { replaceUrl: true });
      return;
    }
    this.check();
  }

  ngOnDestroy(): void {
    this.pending?.unsubscribe();
  }

  private check(): void {
    this.pending = this.ageVerification.confirm(this.verificationId).subscribe({
      next: res => {
        if (res.verified || !res.required) {
          this.state.set('approved');
          this.pending = timer(1500).subscribe(() => this.continue());
          return;
        }

        switch (res.result) {
          case 'failed':
            this.state.set('failed');
            return;
          case 'expired':
            this.state.set('expired');
            return;
          default:
            // started / pending — the user may have come back early, or
            // VerifyMy is still finalising. Poll briefly before offering to resume.
            if (++this.polls < MAX_POLLS) {
              this.state.set('checking');
              this.pending = timer(POLL_INTERVAL_MS).subscribe(() => this.check());
            } else {
              this.state.set('in-progress');
            }
        }
      },
      error: err => {
        this.errorMessage.set(err?.error?.message || 'We couldn’t confirm your verification result.');
        this.state.set('error');
      },
    });
  }

  continue(): void {
    this.router.navigateByUrl(this.ageVerification.consumeReturnUrl(), { replaceUrl: true });
  }

  retry(): void {
    this.router.navigate(['/age-verification'], { replaceUrl: true });
  }
}
